/* eslint-disable no-else-return */
import { useMemo } from "react";
import { useIntl } from "umi";
import { IWordCloudDataItem, IWordCloudChartProps } from "../_config/_interface";
import { handleColorToRGBA } from "@/pages/datalab/utils/utils";
import { getBaseTooltip } from "@/pages/dashboard/components/EchartsTooltip/utils";
import {formatDisplayValue} from "@customComp/Chart/_config/_utils";

/**
 * 用于判断传入的值是否可以转换为数字
 * @param value 要判断的值
 * @returns 如果可以转换为数字，则返回true，否则返回false
 */
const getToNumberState = (value: any) => {
  // 如果传入的值为null或undefined，则返回false
  if (value === null || value === undefined) return false;
  // 如果传入的值为false或true，则返回false
  if (value === false || value === true) return false;
  // 如果传入的值为字符串，并且字符串为空，则返回false
  if (typeof value === 'string' && value.trim() === "") return false;

  // 将传入的值转换为数字
  const num = Number(value);
  // 如果转换后的值为NaN或Infinity，则返回false，否则返回true
  return !isNaN(num) && isFinite(num);
};

/**
 * 用于处理传入的值（datalab百分比数值由后端处理）
 * @param value 要处理的值
 * @param type 类型
 * @returns 处理后的值
 */
const getProcessedValue = (value: any, type?: string) => {
  // 判断传入的type是否为percent，并且value的类型是否为string
  const isPercent = type === 'percent' && typeof value === "string";
  // 如果是percent类型，则去掉百分号
  return isPercent ? value?.replace('%', '') : value;
};

/**
 * 二分查找区间[](](] 
 * @param value 要查找的值
 * @param intervals 频率区间
 * @returns 返回区间的索引，如果找不到则返回-1
 */
const binarySearchInterval = (value: number, arr: any[]) => {
  // 转换成数值类型比较
  const intervals = arr?.map(item => item?.map((v: any) => Number(v)));
  // 如果值小于第一个区间的起始值或大于最后一个区间的结束值，则返回-1
  if (value < intervals[0][0] || value > intervals[intervals.length - 1][1]) {
    return -1;
  }

  // 定义左右指针
  let left = 0;
  let right = intervals.length - 1;

  // 开始二分查找
  while (left <= right) {
    // 计算中间位置
    const mid = Math.floor((left + right) / 2);
    // 获取中间区间的起始值和结束值
    const [start, end] = intervals[mid];

    // 处理第一个区间的特殊情况
    if (mid === 0 && value >= start && value <= end) {
      return mid;
    }

    // 处理其他区间
    if (value > start && value <= end) {
      return mid;
    } else if (value <= start) {
      // 如果值小于等于区间的起始值，则将右指针移到中间位置的前一个位置
      right = mid - 1;
    } else {
      // 如果值大于区间的结束值，则将左指针移到中间位置的后一个位置
      left = mid + 1;
    }
  }

  // 如果没有找到合适的区间，则返回-1
  return -1;
};

/**
 * 生成频率区间
 * @param minValue 最小值
 * @param maxValue 最大值
 * @returns 
 */
const handleIntervals = (minValue: string | number, maxValue: string | number, metricInfo?: { accuracy?: string; value_type?: string; }) => {
  // 将最小值和最大值转换为数字类型
  const min = Number(minValue);
  const max = Number(maxValue);

  const { accuracy: fixNumber } = metricInfo || { accuracy: 0 };
  const isFixed = Number(fixNumber) > 0;

  // 计算原始间隔（最大值减去最小值）
  const originalInterval = max - min;
  // 计算向上取整的间隔（最近的5的整数倍）
  const roundInterval = Math.ceil(originalInterval / 5) * 5;

  // 判断是否使用向上取整的间隔
  const useRoundedInterval = (roundInterval - originalInterval) < (roundInterval / 5);
  // 最终的间隔（使用向上取整的间隔或原始间隔）
  const finalInterval = (useRoundedInterval ? roundInterval : originalInterval);
  // 间隔除以5，得到每个区间的间隔
  const interval = finalInterval / 5;

  const originalMax = max; // 原始最大值
  const adjustedMax = min + interval * 5; // 调整后的最大值

  function preciseAdd(a: number, b: number) {
    const number = parseFloat((a + b).toFixed(12));
    return isFixed ? number.toFixed(Number(fixNumber)) : number;
  }

  // 生成区间（显示时用原始最大值）
  const intervals = [
    [minValue, preciseAdd(min, interval)],
    [preciseAdd(min, interval), preciseAdd(min, 2 * interval)],
    [preciseAdd(min, 2 * interval), preciseAdd(min, 3 * interval)],
    [preciseAdd(min, 3 * interval), preciseAdd(min, 4 * interval)],
    [preciseAdd(min, 4 * interval), maxValue] // 显示原始最大值
  ];

  return {
    min, // 最小值
    max,  // 最大值
    roundInterval, // 向上取整的间隔
    originalInterval, // 原始间隔
    useRoundedInterval, // 是否使用向上取整的间隔
    interval, // 区间间隔
    intervals, // 频率区间
    adjustedMax, // 计算用的调整后最大值
    originalMax,  // 显示用的原始最大值
  };
};

/**
 * 处理词云图数据
 * @param chartData 词云数据
 * @param chartConfig 图表配置
 * @param metricInfo 指标配置
 * @param dimensions 维度数据
 * @returns 处理后词云图数据
 */
const processWordCloudData = (
  chartData: any[],
  chartConfig: IWordCloudChartProps["chartConfig"],
  metricInfo: IWordCloudChartProps["metricInfo"],
  dimensions: { name: string; }[],
) => {
  // 获取valueMetricInfo和colorMetricInfo
  const [valueMetricInfo, colorMetricInfo] = [
    metricInfo[0] || {},
    metricInfo[metricInfo.length - 1] || {}
  ];
  // 获取词云大小value_type
  const { value_type: valueType, metric: valueMetric, accuracy: valueAccuracy, unit: valueUnit } = valueMetricInfo || {};
  // 获取词云颜色value_type
  const { value_type: colorValueType, metric: colorValueMetric, accuracy: colorValueAccuracy, unit: colorValueUnit } = colorMetricInfo || {};
  // 遍历chartData，生成词云数据
  const wordCloudData = (chartData || [])?.map((item: any) => {
    const value = getProcessedValue(item?.[valueMetric] || item?.value, valueType);
    const colorValue = getProcessedValue(item?.[colorValueMetric] || item?.colorValue, colorValueType);
    const dimensionName = item?.[dimensions?.[0]?.name] || "";
    return {
      ...item,
      name: dimensionName?.length > 20 ? `${dimensionName?.slice(0, 20)}...` : dimensionName,
      dimensionName,
      // 词云图value只支持数值类型
      value: getToNumberState(value) ? value : 0,
      colorValue,
      originalName: valueMetric?.replace(/(t[0-2]_)/, ''),
      originalValue: item?.[valueMetric] || "",
      originalValueText: formatDisplayValue({
        value: item?.[valueMetric],
        isPercent: valueType === 'percent',
        accuracy: valueAccuracy || 0,
        unit: valueUnit || 'bmk',
      }),
      originalColorName: colorValueMetric?.replace(/(t[0-2]_)/, ''),
      originalColorValue: item?.[colorValueMetric] || "",
      originalColorValueText: formatDisplayValue({
        value: item?.[colorValueMetric],
        isPercent: colorValueType === 'percent',
        accuracy: colorValueAccuracy || 0,
        unit: colorValueUnit || 'bmk',
      }),
    };
  }) || [];
  return wordCloudData;
};


/**
 * 添加图表设置的颜色和图例
 * @param data 词云数据
 * @param chartConfig 图表配置
 * @param metricInfo 指标配置
 * @param formatMessage 中英文翻译
 * @returns 
 */
const applyColorSettings = (
  data: IWordCloudDataItem[],
  chartConfig: IWordCloudChartProps["chartConfig"],
  metricInfo: IWordCloudChartProps["metricInfo"],
  formatMessage: any
): {
  wordCloudData: IWordCloudDataItem[];
  legend: {
    show: boolean;
    data: {
      name: string;
      color: string;
    }[];
  };
} => {
  const { type, colors = [] } = chartConfig?.colorSetting || {};
  const legend = chartConfig?.legend || { show: false, data: [] };
  let wordCloudData = [...(data || [])];

  switch (type) {
    case 'pureColor':
      wordCloudData = data?.map((item: any) => ({
        ...item,
        color: handleColorToRGBA(colors?.[0]),
      }));
      break;

    case 'multiColor':
      wordCloudData = data?.map((item: any, index: number) => ({
        ...item,
        color: handleColorToRGBA(colors?.[index % colors?.length]),
      }));
      break;

    case 'sentimentRelated':
      return handleSentimentColors(data, chartConfig, metricInfo, formatMessage);

    case 'countRelated':
      return handleCountRelatedColors(data, chartConfig, metricInfo);

    default:
      break;
  }

  return { wordCloudData, legend };
};

/**
 * 处理舆情相关的词云数据颜色和图例
 * @param data 词云数据
 * @param chartConfig 图表配置
 * @param metricInfo 指标配置
 * @param formatMessage 中英文翻译
 * @returns 
 */
const handleSentimentColors = (
  data: IWordCloudDataItem[],
  chartConfig: IWordCloudChartProps["chartConfig"],
  metricInfo: IWordCloudChartProps["metricInfo"],
  formatMessage: any
) => {
  // 颜色取决于最后一个metric字段（datalab限制2个指标）
  const colorMetricInfo = metricInfo?.[metricInfo.length - 1] || {};
  const { value_type: colorValueType, } = colorMetricInfo || {};
  const isPercent = colorValueType === 'percent'; // 判断是否是百分比类型
  // 情感相关的颜色设置
  const legendData = [
    { name: formatMessage({ id: 'dbl_chart_color_setting_sentiment_related_positive' }), color: '#29B569' },
    { name: formatMessage({ id: 'dbl_chart_color_setting_sentiment_related_neutral' }), color: '#6E7794' },
    { name: formatMessage({ id: 'dbl_chart_color_setting_sentiment_related_negative' }), color: '#EF5858' }
  ];
  const legend = {
    show: true,
    data: legendData
  };
  const wordCloudData = data?.map((item: any) => {
    const { colorValue } = item || {};
    const isPositive = isPercent ? (colorValue > 350 && colorValue <= 500) : (colorValue > 3.5 && colorValue <= 5);
    const isNeutral = isPercent ? (colorValue > 250 && colorValue <= 350) : (colorValue > 2.5 && colorValue <= 3.5);
    const isNegative = isPercent ? (colorValue >= 0 && colorValue <= 250) : (colorValue >= 0 && colorValue <= 2.5);
    // eslint-disable-next-line no-nested-ternary
    const legendItem = isPositive ? legendData?.[0] : isNeutral ? legendData?.[1] : isNegative ? legendData?.[2] : undefined;
    if (!legendItem) {
      console.log(item);
      return item; // 如果没有找到合适的区间，返回原始数据
    }
    return {
      ...item,
      color: legendItem?.color,
      category: legendItem?.name,
    };
  });

  return { wordCloudData, legend };
};

/**
 * 处理依据词频的词云数据颜色和图例
 * @param data 词云数据
 * @param chartConfig 图表配置
 * @param metricInfo 指标配置
 * @returns 
 */
const handleCountRelatedColors = (
  data: IWordCloudDataItem[],
  chartConfig: IWordCloudChartProps["chartConfig"],
  metricInfo: IWordCloudChartProps["metricInfo"],
) => {
  const { colors = [] } = chartConfig?.colorSetting || {};
  // 数值相关的颜色设置(第一个metric字段）
  const { value_type: valueType, min, max, } = metricInfo?.[0] || {};
  const isPercent = valueType === 'percent'; // 判断是否是百分比类型
  const minValue = getProcessedValue(min, valueType);
  const maxValue = getProcessedValue(max, valueType);
  const { intervals } = handleIntervals(minValue, maxValue, metricInfo?.[0]);
  const legend = {
    show: min !== max,
    data: intervals?.map((interval: any, index: number) => ({
      name: `${interval[0]}${isPercent ? '%' : ''}~${interval[1]}${isPercent ? '%' : ''}`,
      color: handleColorToRGBA(colors?.[index])
    }))
  };
  const wordCloudData = data?.map((item: any) => {
    const intervalIndex = binarySearchInterval(item?.value, intervals);
    if (intervalIndex === -1) {
      console.log(intervals, item);
      return item; // 如果没有找到合适的区间，返回原始数据
    }
    return {
      ...item,
      color: handleColorToRGBA(colors?.[intervalIndex]),
      category: `${intervals?.[intervalIndex]?.[0]}~${intervals?.[intervalIndex]?.[1]}`,
    };
  });
  return { wordCloudData, legend };
};

// 导出一个名为useWordCloudData的函数，用于处理词云数据
export const useWordCloudData = (props: IWordCloudChartProps) => {
  // 解构props，获取chartData、chartConfig、dimensions和metricInfo
  const { chartData, chartConfig, dimensions, metricInfo } = props || {};
  // 使用useIntl获取formatMessage函数
  const { formatMessage } = useIntl();

  // 使用useMemo缓存词云数据和图例
  const { wordCloudData, legend } = useMemo(() => {
    // 处理词云图数据
    const processedData = processWordCloudData(
      chartData,
      chartConfig,
      metricInfo,
      dimensions,
    );
    // 添加图表设置的颜色和图例
    const { wordCloudData, legend } = applyColorSettings(
      processedData,
      chartConfig,
      metricInfo,
      formatMessage
    );
    return {
      wordCloudData,
      legend,
      isOneMetric: metricInfo.length === 1
    };
  }, [chartConfig, chartData, dimensions, formatMessage, metricInfo]);

  // 返回词云数据和图例
  return {
    wordCloudData,
    legend,
    isOneMetric: metricInfo?.length === 1,
  };
};

export const useWordCloudOptions = (props: IWordCloudChartProps) => {
  const { mergeOptions } = props || {};
  const { wordCloudData, legend, isOneMetric } = useWordCloudData(props);

  const option = useMemo(() => {
    const option = {
      tooltip: {
        show: true,
        trigger: 'item',
        enterable: true,
        confine: true,
        padding: 0,
        borderColor: 'none',
        borderRadius: 'none',
        borderWidth: 0,
        backgroundColor: 'none',
        formatter: (params: any) => {
          const newParams: any = [
            {
              seriesName: params?.data?.originalName,
              seriesValue: params?.data?.originalValue,
              seriesValueText: params?.data?.originalValueText
            },
            {
              seriesName: params?.data?.originalColorName,
              seriesValue: params?.data?.originalColorValue,
              seriesValueText: params?.data?.originalColorValueText
            }
          ];
          return getBaseTooltip({
            params: isOneMetric ? newParams?.slice(0, 1) : newParams,
            options: {
              hasShare: false,
              hasSum: false,
              columns: [
                {
                  header: params?.data?.dimensionName,
                  key: "name",
                  showIcon: false,
                },
                {
                  header: "",
                  key: "seriesValue",
                  render: (_value: any, row: any, _column: any, _icon: any) => row?.orgData?.seriesValueText || '-'
                }
              ]
            }
          });
        }
      },
      legend,
      series: [{
        type: 'wordCloud',
        shape: function shapeSquare(theta: number) {
          return Math.min(
            1 / Math.abs(Math.cos(theta)),
            1 / Math.abs(Math.sin(theta)),
          );
        },
        // 离散分布关键参数
        gridSize: wordCloudData?.length >= 25 ? 20 : 24,
        sizeRange: [12, 48],
        rotationRange: [0, 0, 0, 0], // 旋转角度范围
        drawOutOfBound: false, // 不允许绘制超出边界
        layoutAnimation: true, // 启用布局动画
        textStyle: {
          color(param: { data: { color: string; }; }) {
            return param?.data?.color || '#5086F3';
          },
        },
        emphasis: {
          textStyle: {
            fontWeight: 600,
          },
        },
        left: 0,
        top: 0,
        right: null,
        bottom: null,
        width: '100%',
        height: '100%',
        shrinkToFit: true,
        data: wordCloudData || [],
      }]
    };
    return mergeOptions ? mergeOptions?.(option) : option;
  }, [isOneMetric, legend, mergeOptions, wordCloudData]);

  return option;
};